import { useCallback, useEffect, useState } from "react";
import api, { errMsg } from "@/lib/api";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { BellRing, MessageCircle, MessageSquare, Check } from "lucide-react";

const fmtJour = (d) => (d ? new Date(`${d}T12:00:00`).toLocaleDateString("fr-FR") : "—");
const fmtRappel = (iso) => (iso ? new Date(iso).toLocaleDateString("fr-FR", { day: "2-digit", month: "2-digit", hour: "2-digit", minute: "2-digit" }) : null);

export default function Rappels({ refreshKey }) {
  const [items, setItems] = useState(null);

  const load = useCallback(() => api.get("/membres/rappels").then((r) => setItems(r.data)).catch(() => {}), []);
  useEffect(() => { load(); }, [load, refreshKey]);

  const marquer = async (m) => {
    try {
      await api.post(`/membres/${m.id}/rappel`);
      load();
    } catch (e) {
      toast.error(errMsg(e));
    }
  };

  const envoyer = (m, url) => {
    window.open(url, "_blank", "noopener");
    marquer(m);
  };

  if (!items || items.length === 0) return null;

  return (
    <Card className="bg-card border-border" data-testid="rappels-card">
      <CardHeader className="pb-3">
        <CardTitle className="text-base font-semibold text-foreground flex items-center gap-2">
          <BellRing className="w-4 h-4 text-amber-600 dark:text-amber-400" /> Rappels d'expiration
          <Badge variant="outline" className="border-border text-muted-foreground ml-auto" data-testid="rappels-count">{items.length}</Badge>
        </CardTitle>
        <p className="text-xs text-muted-foreground">Message pré-rédigé : ouvrez WhatsApp ou l'application SMS, vérifiez, puis envoyez.</p>
      </CardHeader>
      <CardContent className="space-y-2">
        {items.map((m) => (
          <div key={m.id} className="flex flex-wrap items-center gap-3 bg-background border border-border rounded-lg px-3 py-2.5" data-testid={`rappel-item-${m.id}`}>
            <div className="min-w-0 flex-1">
              <p className="text-sm font-medium text-foreground truncate">{m.prenom} {m.nom} <span className="text-muted-foreground font-normal">· {m.telephone}</span></p>
              <p className="text-xs text-muted-foreground">
                {m.statut === "expire" ? `Expiré le ${fmtJour(m.date_fin)}` : `Expire le ${fmtJour(m.date_fin)} (dans ${m.jours_restants} j)`}
                {m.dernier_rappel && <span className="ml-2 inline-flex items-center gap-1 text-emerald-600 dark:text-emerald-400" data-testid={`rappel-envoye-${m.id}`}><Check className="w-3 h-3" /> rappel {fmtRappel(m.dernier_rappel)}</span>}
              </p>
            </div>
            <div className="flex items-center gap-2">
              <Button size="sm" variant="outline" className="border-emerald-500/40 text-emerald-600 dark:text-emerald-400 hover:bg-emerald-500/10" data-testid={`rappel-whatsapp-${m.id}`} onClick={() => envoyer(m, m.whatsapp_url)}>
                <MessageCircle className="w-4 h-4 mr-1.5" /> WhatsApp
              </Button>
              <Button size="sm" variant="outline" className="border-sky-500/40 text-sky-600 dark:text-sky-400 hover:bg-sky-500/10" data-testid={`rappel-sms-${m.id}`} onClick={() => envoyer(m, m.sms_url)}>
                <MessageSquare className="w-4 h-4 mr-1.5" /> SMS
              </Button>
            </div>
          </div>
        ))}
      </CardContent>
    </Card>
  );
}
